import React, { useState, useEffect, useRef } from "react";
import { cn, mapEdgesToNodes } from "../../lib/helpers";
import { graphql, useStaticQuery } from "gatsby";
import { LogoBox } from "./logos";

import * as marqueeStyles from "./snowflake/marquee.module.css";

const query = graphql`
  query LogoCarouselQuery {
    companies: allSanityCompany(
      limit: 60
      sort: { fields: [orderRank] }
      filter: {
        status: { eq: "published" }
        mainImage: { asset: { _id: { ne: null } } }
        prospect: { ne: true }
        title: { ne: "Common Room" }
      }
    ) {
      edges {
        node {
          _id
          title
          mainImage {
            ...SanityImageSimple
          }
        }
      }
    }
    stories: allSanityStory(
      filter: {
        status: { eq: "published" }
        slug: { current: { ne: null } }
        company: { mainImage: { asset: { _id: { ne: null } } } }
      }
    ) {
      edges {
        node {
          _id
          company {
            _id
          }
          title
          slug {
            current
          }
        }
      }
    }
  }
`;

export const LogoCarousel = ({ section }) => {
  const data = useStaticQuery(query) || {};
  const companies = mapEdgesToNodes(data.companies);
  const stories = mapEdgesToNodes(data.stories);
  const containerRef = useRef(null);
  const animatedRef = useRef(null);
  const [isInView, setIsInView] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(0);

  // Create a map of company ID to story slug
  const companyStoryMap = new Map();
  stories.forEach(story => {
    if (story.company?._id && story.slug?.current) {
      companyStoryMap.set(story.company._id, `/customers/${story.slug.current}/`);
    }
  });

  // Limit logos if specified in section config
  const logos = section?.limit
    ? companies.slice(0, section.limit)
    : companies;

  // Calculate animation duration based on viewport width
  const getAnimationDuration = (width) => {
    if (width < 640) return 40; // mobile
    if (width < 768) return 50; // sm
    if (width < 1024) return 60; // md
    if (width < 1200) return 70; // lg
    return 80; // xl+
  };

  // Track viewport width
  useEffect(() => {
    const updateWidth = () => setViewportWidth(window.innerWidth);
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Start animation only when carousel enters the viewport
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsInView(true);
      },
      { threshold: 0.1, rootMargin: "0px 0px -10% 0px" }
    );

    observer.observe(containerRef.current);
    return () => {
      if (containerRef.current) observer.unobserve(containerRef.current);
    };
  }, []);

  if (logos.length === 0) return null;

  const renderLogo = (company, keyPrefix) => {
    const storyUrl = companyStoryMap.get(company._id);
    const content = (
      <LogoBox
        alt={company.title}
        image={company.mainImage}
        maxHeight={100}
        left={false}
        fetchPriority="low"
        decoding="async"
      />
    );

    return (
      <div
        key={`${keyPrefix}-${company._id}`}
        className="flex items-center justify-center"
        style={{ minWidth: '180px', maxWidth: '180px', minHeight: '100px' }}
      >
        {storyUrl ? (
          <a
            href={storyUrl}
            className="flex items-center justify-center w-full h-full transform transition-size scale-100 hover:scale-110 duration-200"
          >
            {content}
          </a>
        ) : (
          content
        )}
      </div>
    );
  };

  const animationDuration = getAnimationDuration(viewportWidth);

  return (
    <div ref={containerRef} className="relative w-full overflow-hidden mb-8">
      <div className={cn("flex items-center", marqueeStyles.marquee)}>
        <div
          ref={animatedRef}
          className={cn("flex items-center shrink-0 gap-3", marqueeStyles.marqueeGroup)}
          style={{
            animationDuration: `${animationDuration}s`,
            // animationPlayState avoids layout shift — changing this property is compositor-only,
            // unlike toggling a class that includes min-width which triggers layout recalc
            animationPlayState: isInView ? 'running' : 'paused'
          }}
        >
          {logos.map((company) => renderLogo(company, 'first'))}
          {logos.map((company) => renderLogo(company, 'second'))}
        </div>
      </div>
    </div>
  );
};
